import React, { useEffect, useRef, useCallback } from "react"
import { useLocation } from "react-router-dom"
import PropTypes from "prop-types"

// //Import Scrollbar
import SimpleBar from "simplebar-react"

// MetisMenu
import MetisMenu from "metismenujs"
import withRouter from "components/Common/withRouter"
import { Link } from "react-router-dom"

//i18n
import { withTranslation } from "react-i18next"
import { CanView } from "components/Common/CanView"
import { ACCESS } from "constants/accessPermissions"

const SidebarContent = props => {
  const ref = useRef()
  const clearMenuState = useCallback(menuEl => {
    if (!menuEl) {
      return
    }

    menuEl
      .querySelectorAll(".mm-active, .mm-show, .active")
      .forEach(el => el.classList.remove("mm-active", "mm-show", "active"))

    menuEl
      .querySelectorAll("a[aria-expanded='true']")
      .forEach(el => el.setAttribute("aria-expanded", "false"))
  }, [])

  const activateParentDropdown = useCallback(item => {
    item.classList.add("active")
    const parent = item.parentElement
    const parent2El = parent.childNodes[1]

    if (parent2El && parent2El.id !== "side-menu") {
      parent2El.classList.add("mm-show")
    }

    if (parent) {
      parent.classList.add("mm-active")
      const parent2 = parent.parentElement

      if (parent2) {
        parent2.classList.add("mm-show") // ul tag

        const parent3 = parent2.parentElement // li tag

        if (parent3) {
          parent3.classList.add("mm-active") // li
          parent3.childNodes[0].classList.add("mm-active") //a
          const parent4 = parent3.parentElement // ul
          if (parent4) {
            parent4.classList.add("mm-show") // ul
            const parent5 = parent4.parentElement
            if (parent5) {
              parent5.classList.add("mm-show") // li
              parent5.childNodes[0].classList.add("mm-active") // a tag
            }
          }
        }
      }
      scrollElement(item)
      return false
    }
    scrollElement(item)
    return false
  }, [])

  const path = useLocation()
  const activeMenu = useCallback(() => {
    const pathName = path.pathname
    let matchingMenuItem = null
    const ul = document.getElementById("side-menu")
    if (!ul) {
      return
    }

    const items = ul.getElementsByTagName("a")
    clearMenuState(ul)

    for (let i = 0; i < items.length; ++i) {
      if (pathName === items[i].pathname) {
        matchingMenuItem = items[i]
        break
      }
    }
    if (matchingMenuItem) {
      activateParentDropdown(matchingMenuItem)
    }
  }, [path.pathname, activateParentDropdown, clearMenuState])

  useEffect(() => {
    ref.current.recalculate()
  }, [])

  useEffect(() => {
    const mm = new MetisMenu("#side-menu")
    activeMenu()

    const menuEl = document.getElementById("side-menu")

    const handleClick = e => {
      const link = e.target.closest("a")
      if (link && !link.classList.contains("has-arrow")) {
        if (window.innerWidth < 992) {
          // collapse all open menus
          clearMenuState(menuEl)

          // 🔥 also close the sidebar drawer if you’re using one
          document.body.classList.remove("vertical-collpsed")
          document.body.classList.remove("sidebar-enable")
        }
      }
    }

    menuEl.addEventListener("click", handleClick)

    return () => {
      menuEl.removeEventListener("click", handleClick)
    }
  }, [clearMenuState])

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" })
    activeMenu()
  }, [activeMenu])

  function scrollElement(item) {
    if (item) {
      const currentPosition = item.offsetTop
      if (currentPosition > window.innerHeight) {
        ref.current.getScrollElement().scrollTop = currentPosition - 300
      }
    }
  }

  return (
    <React.Fragment>
      <SimpleBar className="h-100" ref={ref}>
        <div id="sidebar-menu">
          <ul className="metismenu list-unstyled" id="side-menu">
            <li className="menu-title">{props.t("Menu")}</li>

            {/* Dashboard */}
            <CanView access={[ACCESS.DASHBOARD.VIEW_DASHBOARD_PANEL]}>
              <li>
                <Link to="/#" className="has-arrow">
                  <i className="bx bx-home-circle"></i>
                  <span>{props.t("Dashboard")}</span>
                </Link>
                <ul className="sub-menu">
                  <CanView access={[ACCESS.DASHBOARD.VIEW_DASHBOARD]}>
                    <li>
                      <Link to="/dashboard">{props.t("Default")}</Link>
                    </li>
                  </CanView>
                </ul>
              </li>
            </CanView>

            {/* Stock List */}
            <CanView access={[ACCESS.STOCK_PANEL.VIEW_PANEL]}>
              <li>
                <Link to="/#" className="has-arrow">
                  <i className="bx bx-car"></i>
                  <span>{props.t("Vehicle Management")}</span>
                </Link>
                <ul className="sub-menu">
                  <li>
                    <Link to="/stock-list">{props.t("Stock List")}</Link>
                  </li>

                  <CanView access={[ACCESS.BRAND.VIEW_BRAND_LIST]}>
                    <li>
                      <Link to="/brand-list">{props.t("Brands")}</Link>
                    </li>
                  </CanView>
                  <CanView access={[ACCESS.MODEL.VIEW_MODEL_LIST]}>
                    <li>
                      <Link to="/model-list">{props.t("Models")}</Link>
                    </li>
                  </CanView>
                  <CanView access={[ACCESS.LOCATION.VIEW_LOCATION_LIST]}>
                    <li>
                      <Link to="/location-list">{props.t("Locations")}</Link>
                    </li>
                  </CanView>
                  <CanView access={[ACCESS.STATUS.VIEW_STATUS_LIST]}>
                    <li>
                      <Link to="/status-list">{props.t("Statuses")}</Link>
                    </li>
                  </CanView>
                  <CanView access={[ACCESS.COLOR.VIEW_COLOR_LIST]}>
                    <li>
                      <Link to="/color-list">{props.t("Colors")}</Link>
                    </li>
                  </CanView>
                  <CanView access={[ACCESS.FEATURE.VIEW_FEATURE_LIST]}>
                    <li>
                      <Link to="/feature-list">{props.t("Features")}</Link>
                    </li>
                  </CanView>
                  <CanView access={[ACCESS.PACKAGE.VIEW_PACKAGE_LIST]}>
                    <li>
                      <Link to="/package-list">{props.t("Packages")}</Link>
                    </li>
                  </CanView>
                  <CanView access={[ACCESS.YEAR.VIEW_YEAR_LIST]}>
                    <li>
                      <Link to="/year-list">{props.t("Years")}</Link>
                    </li>
                  </CanView>
                  <CanView access={[ACCESS.ENGINE_CC.VIEW_ENGINE_CC_LIST]}>
                    <li>
                      <Link to="/engine-cc-list">{props.t("Engine CCs")}</Link>
                    </li>
                  </CanView>
                  <CanView
                    access={[ACCESS.CHASSIS_CODE.VIEW_CHASSIS_CODE_LIST]}
                  >
                    <li>
                      <Link to="/chassis-code-list">
                        {props.t("Chassis Codes")}
                      </Link>
                    </li>
                  </CanView>
                  <CanView access={[ACCESS.ENGINE_CODE.VIEW_ENGINE_CODE_LIST]}>
                    <li>
                      <Link to="/engine-code-list">
                        {props.t("Engine Codes")}
                      </Link>
                    </li>
                  </CanView>
                  <CanView access={[ACCESS.FUEL_TYPE.VIEW_FUEL_TYPE_LIST]}>
                    <li>
                      <Link to="/fuel-type-list">{props.t("Fuel Types")}</Link>
                    </li>
                  </CanView>
                  <CanView access={[ACCESS.ENGINE_TYPE.VIEW_ENGINE_TYPE_LIST]}>
                    <li>
                      <Link to="/engine-type-list">
                        {props.t("Engine Types")}
                      </Link>
                    </li>
                  </CanView>
                  <CanView access={[ACCESS.KEY_TYPE.VIEW_KEY_TYPE_LIST]}>
                    <li>
                      <Link to="/key-type-list">{props.t("Key Types")}</Link>
                    </li>
                  </CanView>
                  <CanView
                    access={[ACCESS.AUCTION_GRADE.VIEW_AUCTION_GRADE_LIST]}
                  >
                    <li>
                      <Link to="/auction-grade-list">
                        {props.t("Auction Grades")}
                      </Link>
                    </li>
                  </CanView>
                  <CanView
                    access={[ACCESS.TRANSMISSION.VIEW_TRANSMISSION_LIST]}
                  >
                    <li>
                      <Link to="/transmission-list">
                        {props.t("Transmissions")}
                      </Link>
                    </li>
                  </CanView>
                </ul>
              </li>
            </CanView>

            {/* Vehicle Movement Group */}
            <CanView access={[ACCESS.VEHICLE_MOVEMENT_PANEL.VIEW]}>
              <li>
                <Link to="/#" className="has-arrow">
                  <i className="bx bx-transfer-alt"></i>
                  <span>{props.t("Vehicle Movement")}</span>
                </Link>
                <ul className="sub-menu">
                  <CanView access={[ACCESS.VEHICLE_MOVEMENT.VIEW_LIST]}>
                    <li>
                      <Link to={"/vehicle-movement-list?tab=movement-list"}>
                        {props.t("Movement List")}
                      </Link>
                    </li>
                  </CanView>
                  <CanView access={[ACCESS.VEHICLE_MOVEMENT.VIEW_KEY_LIST]}>
                    <li>
                      <Link to={"/vehicle-movement-list?tab=key-list"}>
                        {props.t("Movement Key List")}
                      </Link>
                    </li>
                  </CanView>
                  <CanView access={[ACCESS.VEHICLE_MOVEMENT.VIEW_DAILY_AUDIT]}>
                    <li>
                      <Link to={"/vehicle-movement-list?tab=daily-audit"}>
                        {props.t("Daily Vehicle Audit")}
                      </Link>
                    </li>
                  </CanView>

                  <CanView access={[ACCESS.VEHICLE_MOVEMENT_TYPE.VIEW_LIST]}>
                    <li>
                      <Link to="/vehicle-movement-type-list">
                        {props.t("Movement Type List")}
                      </Link>
                    </li>
                  </CanView>
                  <CanView access={[ACCESS.LOCATION.VIEW_LOCATION_LIST]}>
                    <li>
                      <Link to="/movement-location-list">
                        {props.t("Location List")}
                      </Link>
                    </li>
                  </CanView>
                </ul>
              </li>
            </CanView>
            {/* Vehicle Fuel Group */}
            <CanView access={[ACCESS.VEHICLE_FUEL_PANEL.VIEW]}>
              <li>
                <Link to="/#" className="has-arrow">
                  <i className="fas fa-gas-pump"></i>
                  <span>{props.t("Vehicle Fuel")}</span>
                </Link>
                <ul className="sub-menu">
                  <CanView access={[ACCESS.VEHICLE_FUEL_USE.VIEW_LIST]}>
                    <li>
                      <Link to="/fuel-usage-list">
                        {props.t("Fuel Usage List")}
                      </Link>
                    </li>
                  </CanView>

                  <CanView access={[ACCESS.VEHICLE_FUEL_STOCK.VIEW_LIST]}>
                    <li>
                      <Link to={"/purchased-fuel-list"}>
                        {props.t("Purchased Fuel List")}
                      </Link>
                    </li>
                  </CanView>
                  <CanView access={[ACCESS.LOCATION.VIEW_LOCATION_LIST]}>
                    <li>
                      <Link to="/fuel-station-list">
                        {props.t("Fuel Station List")}
                      </Link>
                    </li>
                  </CanView>
                </ul>
              </li>
            </CanView>

            {/* Content Manager */}
            <CanView access={[ACCESS.CONTENT_MANAGER.VIEW_CONTENT_MANAGER]}>
              <li>
                <Link to="/#" className="has-arrow">
                  <i className="bx bx-notepad"></i>
                  <span>{props.t("Content Manager")}</span>
                </Link>
                <ul className="sub-menu">
                  <CanView
                    access={[ACCESS.CONTENT_MANAGER.VIEW_VEHICLE_CONTENT_LIST]}
                  >
                    <li>
                      <Link to="/vehicle-content-list">
                        {props.t("Vehicle Content List")}
                      </Link>
                    </li>
                  </CanView>
                </ul>
              </li>
            </CanView>

            {/* Customer */}
            <CanView access={[ACCESS.CUSTOMER_PANEL.VIEW]}>
              <li>
                <Link to="/#" className="has-arrow">
                  <i className="bx bx-group"></i>
                  <span>{props.t("Customers")}</span>
                </Link>
                <ul className="sub-menu">
                  <CanView access={[ACCESS.CUSTOMER.VIEW_LIST]}>
                    <li>
                      <Link to="/customer-list">
                        {props.t("Customer List")}
                      </Link>
                    </li>
                  </CanView>
                </ul>
              </li>
            </CanView>

            {/* Quotation */}
            <CanView access={[ACCESS.CUSTOMER.VIEW_LIST]}>
              <li>
                <Link to="/#" className="has-arrow">
                  <i className="bx bx-file"></i>
                  <span>{props.t("Quotation")}</span>
                </Link>
                <ul className="sub-menu">
                  <CanView access={[ACCESS.CUSTOMER.VIEW_LIST]}>
                    <li>
                      <Link to="/quotation-list">
                        {props.t("Quotation List")}
                      </Link>
                    </li>
                  </CanView>
                  <CanView access={[ACCESS.CUSTOMER.VIEW_LIST]}>
                    <li>
                      <Link to="/quotation-list/add">
                        {props.t("Create Quotation")}
                      </Link>
                    </li>
                  </CanView>
                  <CanView access={[ACCESS.CUSTOMER.VIEW_LIST]}>
                    <li>
                      <Link to="/company-pads">
                        {props.t("Company Pads")}
                      </Link>
                    </li>
                  </CanView>
                </ul>
              </li>
            </CanView>

            {/* Money Receipt */}
            <CanView access={[ACCESS.MONEY_RECEIPT_PANEL.VIEW]}>
              <li>
                <Link to="/#" className="has-arrow">
                  <i className="bx bx-receipt"></i>
                  <span>{props.t("Money Receipt")}</span>
                </Link>
                <ul className="sub-menu">
                  <CanView access={[ACCESS.MONEY_RECEIPT.VIEW_LIST]}>
                    <li>
                      <Link to="/money-receipts">
                        {props.t("Money Receipt List")}
                      </Link>
                    </li>
                  </CanView>
                  <CanView access={[ACCESS.MONEY_RECEIPT.CREATE]}>
                    <li>
                      <Link to="/money-receipts/create">
                        {props.t("Create Money Receipt")}
                      </Link>
                    </li>
                  </CanView>
                </ul>
              </li>
            </CanView>

            {/* Pre-Order */}
            <CanView access={[ACCESS.PRE_ORDER.VIEW_LIST]}>
              <li>
                <Link to="/#" className="has-arrow">
                  <i className="bx bx-calendar-check"></i>
                  <span>{props.t("Pre-Order")}</span>
                </Link>
                <ul className="sub-menu">
                  <CanView access={[ACCESS.PRE_ORDER.VIEW_LIST]}>
                    <li>
                      <Link to="/pre-orders">
                        {props.t("Pre-Order List")}
                      </Link>
                    </li>
                  </CanView>
                  <CanView access={[ACCESS.PRE_ORDER.CREATE]}>
                    <li>
                      <Link to="/pre-orders/create">
                        {props.t("Create Pre-Order")}
                      </Link>
                    </li>
                  </CanView>
                </ul>
              </li>
            </CanView>

            {/* Car Care */}
            <CanView
              access={[
                ACCESS.SERVICE_INVOICE.VIEW_LIST,
                ACCESS.CAR_CARE_SERVICE.VIEW_LIST,
                ACCESS.CAR_CARE_SERVICE_CENTER.VIEW_LIST,
                ACCESS.CAR_CARE_ACCOUNT.VIEW_LIST,
                ACCESS.ACCESSORIES_MANAGEMENT.VIEW,
                ACCESS.ACCESSORIES_USAGE.VIEW_LIST,
                ACCESS.ACCESSORY_USE_REQUEST.VIEW_LIST,
                ACCESS.ACCESSORIES_STOCK.VIEW_LIST,
                ACCESS.ACCESSORIES.VIEW_LIST,
                ACCESS.ACCESSORIES_COMPANY.VIEW_LIST,
                ACCESS.UNIT.VIEW_LIST,
              ]}
            >
              <li>
                <Link to="/#" className="has-arrow">
                  <i className="bx bx-car"></i>
                  <span>{props.t("Car Care")}</span>
                </Link>
                <ul className="sub-menu">
                  <CanView access={[ACCESS.SERVICE_INVOICE.VIEW_LIST]}>
                    <li>
                      <Link to="/service-invoices">
                        {props.t("Service Invoices")}
                      </Link>
                    </li>
                  </CanView>
                  <CanView access={[ACCESS.CAR_CARE_SERVICE.VIEW_LIST]}>
                    <li>
                      <Link to="/car-care-services">
                        {props.t("Service Catalog")}
                      </Link>
                    </li>
                  </CanView>
                  <CanView access={[ACCESS.CAR_CARE_SERVICE_CENTER.VIEW_LIST]}>
                    <li>
                      <Link to="/car-care-service-centers">
                        {props.t("Service Centers")}
                      </Link>
                    </li>
                  </CanView>
                  <CanView access={[ACCESS.CAR_CARE_ACCOUNT.VIEW_LIST]}>
                    <li>
                      <Link to="/car-care-accounts">
                        {props.t("Car Care Accounts")}
                      </Link>
                    </li>
                  </CanView>
                  <CanView access={[ACCESS.ACCESSORIES_USAGE.VIEW_LIST]}>
                    <li>
                      <Link to="/accessories-usage-list">
                        {props.t("Accessory Usage List")}
                      </Link>
                    </li>
                  </CanView>
                  <CanView access={[ACCESS.ACCESSORY_USE_REQUEST.VIEW_LIST]}>
                    <li>
                      <Link to="/accessory-use-requests">
                        {props.t("Accessory Use Requests")}
                      </Link>
                    </li>
                  </CanView>
                  <CanView access={[ACCESS.ACCESSORIES_STOCK.VIEW_LIST]}>
                    <li>
                      <Link to="/accessories-stock-list">
                        {props.t("Accessory Stock List")}
                      </Link>
                    </li>
                  </CanView>
                  <CanView access={[ACCESS.ACCESSORIES.VIEW_LIST]}>
                    <li>
                      <Link to="/accessories-list">
                        {props.t("Accessories List")}
                      </Link>
                    </li>
                  </CanView>
                  <CanView access={[ACCESS.ACCESSORIES_COMPANY.VIEW_LIST]}>
                    <li>
                      <Link to="/accessories-store-list">
                        {props.t("Accessory Store List")}
                      </Link>
                    </li>
                  </CanView>
                  <CanView access={[ACCESS.UNIT.VIEW_LIST]}>
                    <li>
                      <Link to="/accessories-unit-list">
                        {props.t("Accessory Unit List")}
                      </Link>
                    </li>
                  </CanView>
                </ul>
              </li>
            </CanView>

            {/* Vehicle Stock Inventory */}
            <CanView
              access={[
                ACCESS.VEHICLE_STOCK_INVENTORY.VIEW_LIST,
                ACCESS.INVENTORY_ACCESSORY.VIEW_LIST,
              ]}
            >
              <li>
                <Link to="/#" className="has-arrow">
                  <i className="bx bx-clipboard"></i>
                  <span>{props.t("Inventory")}</span>
                </Link>
                <ul className="sub-menu">
                  <CanView access={[ACCESS.VEHICLE_STOCK_INVENTORY.VIEW_LIST]}>
                    <li>
                      <Link to="/vehicle-stock-inventory-list">
                        {props.t("Inventory List")}
                      </Link>
                    </li>
                  </CanView>
                  <CanView access={[ACCESS.INVENTORY_ACCESSORY.VIEW_LIST]}>
                    <li>
                      <Link to="/inventory-accessory-list">
                        {props.t("Inventory Accessories")}
                      </Link>
                    </li>
                  </CanView>
                </ul>
              </li>
            </CanView>

            {/* Petty Cash */}
            <CanView
              access={[
                ACCESS.PETTY_CASH_PAYMENT_FROM.VIEW_LIST,
                ACCESS.PETTY_CASH_PAYMENT_MODE.VIEW_LIST,
                ACCESS.PETTY_CASH_PAYMENT_PURPOSE.VIEW_LIST,
                ACCESS.PETTY_CASH_TRANSACTION.VIEW_LIST,
                ACCESS.PETTY_CASH_REPORT.VIEW_LIST,
              ]}
            >
              <li>
                <Link to="/#" className="has-arrow">
                  <i className="bx bx-money"></i>
                  <span>{props.t("Petty Cash")}</span>
                </Link>
                <ul className="sub-menu">
                  <CanView access={[ACCESS.PETTY_CASH_TRANSACTION.VIEW_LIST]}>
                    <li>
                      <Link to="/petty-cash-transaction-list">
                        {props.t("Transactions")}
                      </Link>
                    </li>
                  </CanView>

                  <CanView access={[ACCESS.PETTY_CASH_BALANCE.VIEW_LIST]}>
                    <li>
                      <Link to="/petty-cash-balance">
                        {props.t("Current Balance")}
                      </Link>
                    </li>
                  </CanView>
                  <CanView access={[ACCESS.PETTY_CASH_PAYMENT_FROM.VIEW_LIST]}>
                    <li>
                      <Link to="/petty-cash-payment-from-list">
                        {props.t("Payment From")}
                      </Link>
                    </li>
                  </CanView>
                  <CanView access={[ACCESS.PETTY_CASH_PAYMENT_MODE.VIEW_LIST]}>
                    <li>
                      <Link to="/petty-cash-payment-mode-list">
                        {props.t("Payment Mode")}
                      </Link>
                    </li>
                  </CanView>
                  <CanView
                    access={[ACCESS.PETTY_CASH_PAYMENT_PURPOSE.VIEW_LIST]}
                  >
                    <li>
                      <Link to="/petty-cash-payment-purpose-list">
                        {props.t("Payment Purpose")}
                      </Link>
                    </li>
                  </CanView>
                  <CanView access={[ACCESS.PETTY_CASH_REPORT.VIEW_LIST]}>
                    <li>
                      <Link to="/petty-cash-reports">{props.t("Reports")}</Link>
                    </li>
                  </CanView>
                </ul>
              </li>
            </CanView>

            {/* Account Management */}
            <CanView access={[ACCESS.ACCOUNT_MANAGEMENT.VIEW]}>
              <li>
                <Link to="/#" className="has-arrow">
                  <i className="bx bx-wallet"></i>
                  <span>{props.t("Account Management")}</span>
                </Link>
                <ul className="sub-menu">
                  <CanView access={[ACCESS.ACCOUNT_MANAGEMENT.VIEW, ACCESS.ACCOUNT_VENDOR_BILL.VIEW_LIST, ACCESS.ACCOUNT_VENDOR_PAYMENT.VIEW_LIST, ACCESS.ACCOUNT_CUSTOMER_PAYMENT.VIEW_LIST, ACCESS.ACCOUNT_LOAN.VIEW_LIST, ACCESS.ACCOUNT_LOAN_REPAYMENT.VIEW_LIST, ACCESS.ACCOUNT_TRANSACTION.VIEW_LIST, ACCESS.ACCOUNT_EXPENSE.VIEW_LIST, ACCESS.ACCOUNT_FUND_TRANSFER.VIEW_LIST, ACCESS.ACCOUNT.VIEW_LIST]}>
                    <li>
                      <Link to="/account-workspace">
                        {props.t("Finance Workspace")}
                      </Link>
                    </li>
                  </CanView>
                  <CanView access={[ACCESS.ACCOUNT_PAYMENT_METHOD.VIEW_LIST]}>
                    <li>
                      <Link to="/account-payment-method-list">
                        {props.t("Payment Method List")}
                      </Link>
                    </li>
                  </CanView>
                  <CanView access={[ACCESS.ACCOUNT_BANK_ACCOUNT.VIEW_LIST]}>
                    <li>
                      <Link to="/account-bank-account-list">
                        {props.t("Bank Account List")}
                      </Link>
                    </li>
                  </CanView>
                  <CanView access={[ACCESS.ACCOUNT_ACCOUNT_TYPE.VIEW_LIST]}>
                    <li>
                      <Link to="/account-account-type-list">
                        {props.t("Account Type List")}
                      </Link>
                    </li>
                  </CanView>
                  <CanView access={[ACCESS.ACCOUNT_TRANSACTION_TYPE.VIEW_LIST]}>
                    <li>
                      <Link to="/account-transaction-type-list">
                        {props.t("Transaction Type List")}
                      </Link>
                    </li>
                  </CanView>
                  <CanView access={[ACCESS.ACCOUNT_EXPENSE_TYPE.VIEW_LIST]}>
                    <li>
                      <Link to="/account-expense-type-list">
                        {props.t("Expense Type List")}
                      </Link>
                    </li>
                  </CanView>
                  <CanView access={[ACCESS.ACCOUNT_LOAN_STATUS.VIEW_LIST]}>
                    <li>
                      <Link to="/account-loan-status-list">
                        {props.t("Loan Status List")}
                      </Link>
                    </li>
                  </CanView>
                  <CanView access={[ACCESS.ACCOUNT_BILL_STATUS.VIEW_LIST]}>
                    <li>
                      <Link to="/account-bill-status-list">
                        {props.t("Bill Status List")}
                      </Link>
                    </li>
                  </CanView>
                  <CanView access={[ACCESS.ACCOUNT.VIEW_LIST]}>
                    <li>
                      <Link to="/account-list">{props.t("Account List")}</Link>
                    </li>
                  </CanView>
                  <CanView access={[ACCESS.ACCOUNT_VENDOR.VIEW_LIST]}>
                    <li>
                      <Link to="/account-vendor-list">
                        {props.t("Vendor List")}
                      </Link>
                    </li>
                  </CanView>
                  <CanView access={[ACCESS.ACCOUNT_EXPENSE.VIEW_LIST]}>
                    <li>
                      <Link to="/account-expense-list">
                        {props.t("Expense List")}
                      </Link>
                    </li>
                  </CanView>
                  <CanView access={[ACCESS.ACCOUNT_RTGS_PAYMENT.VIEW_LIST]}>
                    <li>
                      <Link to="/account-rtgs-payment-list">
                        {props.t("RTGS Payments")}
                      </Link>
                    </li>
                  </CanView>
                  <CanView access={[ACCESS.RTGS_BANK_TEMPLATE.VIEW_LIST]}>
                    <li>
                      <Link to="/rtgs-bank-template-list">
                        {props.t("RTGS Bank Templates")}
                      </Link>
                    </li>
                  </CanView>
                  <CanView access={[ACCESS.ACCOUNT_CUSTOMER_PAYMENT.VIEW_LIST]}>
                    <li>
                      <Link to="/account-customer-payment-list">
                        {props.t("Customer Payment List")}
                      </Link>
                    </li>
                  </CanView>
                  <CanView access={[ACCESS.ACCOUNT_CUSTOMER_REFUND.VIEW_LIST]}>
                    <li>
                      <Link to="/account-customer-refund-list">
                        {props.t("Customer Refund List")}
                      </Link>
                    </li>
                  </CanView>
                  <CanView access={[ACCESS.ACCOUNT_VENDOR_BILL.VIEW_LIST]}>
                    <li>
                      <Link to="/account-vendor-bill-list">
                        {props.t("Vendor Bill List")}
                      </Link>
                    </li>
                  </CanView>
                  <CanView access={[ACCESS.ACCOUNT_VENDOR_PAYMENT.VIEW_LIST]}>
                    <li>
                      <Link to="/account-vendor-payment-list">
                        {props.t("Vendor Payment List")}
                      </Link>
                    </li>
                  </CanView>
                  <CanView access={[ACCESS.ACCOUNT_LOAN.VIEW_LIST]}>
                    <li>
                      <Link to="/account-loan-list">
                        {props.t("Loan List")}
                      </Link>
                    </li>
                  </CanView>
                  <CanView access={[ACCESS.ACCOUNT_LOAN_REPAYMENT.VIEW_LIST]}>
                    <li>
                      <Link to="/account-loan-repayment-list">
                        {props.t("Loan Repayment List")}
                      </Link>
                    </li>
                  </CanView>
                  <CanView access={[ACCESS.ACCOUNT_TRANSACTION.VIEW_LIST]}>
                    <li>
                      <Link to="/account-dashboard">
                        {props.t("Account Dashboard")}
                      </Link>
                    </li>
                  </CanView>
                  <CanView access={[ACCESS.ACCOUNT_TRANSACTION.VIEW_LIST]}>
                    <li>
                      <Link to="/account-daily-report">
                        {props.t("Daily Report")}
                      </Link>
                    </li>
                  </CanView>
                  <CanView access={[ACCESS.ACCOUNT_DAY_CLOSE.VIEW_LIST]}>
                    <li>
                      <Link to="/account-day-close-list">
                        {props.t("Day Close History")}
                      </Link>
                    </li>
                  </CanView>
                  <CanView access={[ACCESS.ACCOUNT_TRANSACTION.VIEW_LIST]}>
                    <li>
                      <Link to="/account-transaction-list">
                        {props.t("Transaction Ledger")}
                      </Link>
                    </li>
                  </CanView>
                  <CanView access={[ACCESS.ACCOUNT_FUND_TRANSFER.VIEW_LIST]}>
                    <li>
                      <Link to="/account-fund-transfer-list">
                        {props.t("Fund Transfer List")}
                      </Link>
                    </li>
                  </CanView>
                  <CanView access={[ACCESS.ACCOUNT_VEHICLE_PURCHASE.VIEW_LIST]}>
                    <li>
                      <Link to="/account-vehicle-purchase-list">
                        {props.t("Vehicle Purchase List")}
                      </Link>
                    </li>
                  </CanView>
                  <CanView access={[ACCESS.ACCOUNT_IMPORTER_PAYMENT.VIEW_LIST]}>
                    <li>
                      <Link to="/account-importer-payment-list">
                        {props.t("Importer Payment List")}
                      </Link>
                    </li>
                  </CanView>

                  {/* Audit Log */}
                  <CanView access={[ACCESS.ACCOUNT_AUDIT_LOG.VIEW_LIST]}>
                    <li>
                      <Link to="/audit-log-list">{props.t("Audit Log")}</Link>
                    </li>
                  </CanView>

                  {/* API Status & Features */}
                  <CanView access={[ACCESS.ACCOUNT_API_STATUS.VIEW]}>
                    <li>
                      <Link to="/api-status">
                        {props.t("API Status & Features")}
                      </Link>
                    </li>
                  </CanView>
                </ul>
              </li>
            </CanView>

            {/* Importer List */}
            <CanView
              access={[ACCESS.VEHICLE_IMPORTER.VIEW_VEHICLE_IMPORTER_LIST]}
            >
              <li>
                <Link to="/importer-list">
                  <i className="bx bx-store"></i>
                  <span>{props.t("Importer List")}</span>
                </Link>
              </li>
            </CanView>

            {/* Message List */}
            <CanView access={[ACCESS.MESSAGE.VIEW_CONTACT_MESSAGE_LIST]}>
              <li>
                <Link to="/message-list">
                  <i className="bx bx-envelope"></i>
                  <span>{props.t("Message List")}</span>
                </Link>
              </li>
            </CanView>

            {/* Employee Management */}
            <CanView
              access={[
                ACCESS.EMPLOYEE_MANAGEMENT.VIEW_PANEL,
                ACCESS.EMPLOYEE.VIEW_LIST,
                ACCESS.EMPLOYEE_DEPARTMENT.VIEW_LIST,
                ACCESS.EMPLOYEE_DESIGNATION.VIEW_LIST,
              ]}
            >
              <li>
                <Link to="/#" className="has-arrow">
                  <i className="bx bx-group"></i>
                  <span>{props.t("Employees")}</span>
                </Link>
                <ul className="sub-menu">
                  <CanView access={[ACCESS.EMPLOYEE.VIEW_LIST]}>
                    <li>
                      <Link to="/employee-list">
                        {props.t("Employee List")}
                      </Link>
                    </li>
                  </CanView>
                  <CanView
                    access={[ACCESS.EMPLOYEE_DEPARTMENT.VIEW_LIST]}
                  >
                    <li>
                      <Link to="/employee-departments">
                        {props.t("Departments")}
                      </Link>
                    </li>
                  </CanView>
                  <CanView
                    access={[ACCESS.EMPLOYEE_DESIGNATION.VIEW_LIST]}
                  >
                    <li>
                      <Link to="/employee-designations">
                        {props.t("Designations")}
                      </Link>
                    </li>
                  </CanView>
                </ul>
              </li>
            </CanView>

            {/* Attendance */}
            <CanView
              access={[
                ACCESS.ATTENDANCE.VIEW_PANEL,
                ACCESS.ATTENDANCE.CHECK_IN,
                ACCESS.ATTENDANCE.VIEW_LIST,
                ACCESS.ATTENDANCE.VIEW_SETTINGS,
              ]}
            >
              <li>
                <Link to="/#" className="has-arrow">
                  <i className="bx bx-time-five"></i>
                  <span>{props.t("Attendance")}</span>
                </Link>
                <ul className="sub-menu">
                  <CanView access={[ACCESS.ATTENDANCE.CHECK_IN]}>
                    <li>
                      <Link to="/attendance/my">
                        {props.t("My Attendance")}
                      </Link>
                    </li>
                  </CanView>
                  <CanView access={[ACCESS.ATTENDANCE.VIEW_LIST]}>
                    <li>
                      <Link to="/attendance">
                        {props.t("Attendance List")}
                      </Link>
                    </li>
                  </CanView>
                  <CanView access={[ACCESS.ATTENDANCE.VIEW_SETTINGS]}>
                    <li>
                      <Link to="/attendance/settings">
                        {props.t("Settings")}
                      </Link>
                    </li>
                  </CanView>
                  <CanView access={[ACCESS.ATTENDANCE.VIEW_REPORT]}>
                    <li>
                      <Link to="/attendance/report">
                        {props.t("Monthly Report")}
                      </Link>
                    </li>
                  </CanView>
                </ul>
              </li>
            </CanView>

            {/* Leave */}
            <CanView
              access={[
                ACCESS.LEAVE.VIEW_PANEL,
                ACCESS.LEAVE.VIEW_LIST,
                ACCESS.LEAVE.MANAGE_TYPES,
                ACCESS.LEAVE.MANAGE_HOLIDAYS,
                ACCESS.LEAVE.MANAGE_BALANCES,
              ]}
            >
              <li>
                <Link to="/#" className="has-arrow">
                  <i className="bx bx-calendar-check"></i>
                  <span>{props.t("Leave")}</span>
                </Link>
                <ul className="sub-menu">
                  <CanView access={[ACCESS.LEAVE.VIEW_PANEL]}>
                    <li>
                      <Link to="/leave/my">{props.t("My Leave")}</Link>
                    </li>
                  </CanView>
                  <CanView access={[ACCESS.LEAVE.VIEW_LIST]}>
                    <li>
                      <Link to="/leave/requests">{props.t("Leave Requests")}</Link>
                    </li>
                  </CanView>
                  <CanView access={[ACCESS.LEAVE.MANAGE_TYPES]}>
                    <li>
                      <Link to="/leave/types">{props.t("Leave Types")}</Link>
                    </li>
                  </CanView>
                  <CanView access={[ACCESS.LEAVE.MANAGE_HOLIDAYS]}>
                    <li>
                      <Link to="/leave/holidays">{props.t("Holidays")}</Link>
                    </li>
                  </CanView>
                  <CanView access={[ACCESS.LEAVE.MANAGE_BALANCES]}>
                    <li>
                      <Link to="/leave/balances">{props.t("Balances")}</Link>
                    </li>
                  </CanView>
                </ul>
              </li>
            </CanView>

            {/* Showrooms */}
            <CanView access={[ACCESS.SHOWROOM_PANEL.VIEW_PANEL]}>
              <li>
                <Link to="/#" className="has-arrow">
                  <i className="bx bx-building-house"></i>
                  <span>{props.t("Showrooms")}</span>
                </Link>
                <ul className="sub-menu">
                  <CanView access={[ACCESS.SHOWROOM.VIEW_SHOWROOM_LIST]}>
                    <li>
                      <Link to="/showroom-list">
                        {props.t("Showroom List")}
                      </Link>
                    </li>
                  </CanView>
                </ul>
              </li>
            </CanView>

            {/* Users */}
            <CanView access={[ACCESS.USER_PANEL.VIEW_PANEL]}>
              <li>
                <Link to="/#" className="has-arrow">
                  <i className="bx bx-user"></i>
                  <span>{props.t("Users")}</span>
                </Link>
                <ul className="sub-menu">
                  <CanView access={[ACCESS.USER.VIEW_USER_LIST]}>
                    <li>
                      <Link to="/user-list">{props.t("User List")}</Link>
                    </li>
                  </CanView>
                </ul>
              </li>
            </CanView>

            {/* Settings */}
            <CanView access={[ACCESS.SETTINGS.VIEW_SETTINGS_PANEL]}>
              <li>
                <Link to="/#" className="has-arrow">
                  <i className="bx bx-cog"></i>
                  <span>{props.t("Settings")}</span>
                </Link>

                <ul className="sub-menu">
                  <CanView access={[ACCESS.ACTIVITY_LOG.VIEW_LIST]}>
                    <li>
                      <Link to="/activity-log-list">{props.t("Activity Log")}</Link>
                    </li>
                  </CanView>

                  {/* Role Permission Group */}
                  <CanView
                    access={[ACCESS.ROLE_PERMISSION.VIEW_ROLE_PERMISSION]}
                  >
                    <li>
                      <Link to="/#" className="has-arrow">
                        {props.t("Role Permission")}
                      </Link>
                      <ul className="sub-menu">
                        <CanView access={[ACCESS.MODULE.VIEW_MODULE_LIST]}>
                          <li>
                            <Link to="/module-list">
                              {props.t("Module List")}
                            </Link>
                          </li>
                        </CanView>
                        <CanView
                          access={[ACCESS.PERMISSION.VIEW_PERMISSION_LIST]}
                        >
                          <li>
                            <Link to="/permission-list">
                              {props.t("Permission List")}
                            </Link>
                          </li>
                        </CanView>

                        <CanView access={[ACCESS.ROLE.VIEW_ROLE_LIST]}>
                          <li>
                            <Link to="/role-list">{props.t("Role List")}</Link>
                          </li>
                        </CanView>
                      </ul>
                    </li>
                  </CanView>
                </ul>
              </li>
            </CanView>
          </ul>
        </div>
      </SimpleBar>
    </React.Fragment>
  )
}

SidebarContent.propTypes = {
  location: PropTypes.object,
  t: PropTypes.any,
}

export default withRouter(withTranslation()(SidebarContent))
